import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { CEP } from 'cep-promise'
import { PackageDataHomeV2 } from 'components/modules/patientArea/home/homeV2/types'
import { CourseSlug, PackageData, PsychologyMedium } from 'components/modules/patientArea/home/packages/types'
import { flatten, groupBy, isArray, mergeWith, uniq, uniqBy } from 'lodash'
import nestApiService from 'services/api/nest-api-service'

export interface PackagesState {
  isLoading: boolean
  packages: PackageData[]
  activePackage: PackageData | null
  activePsychologyVariant: PsychologyVariantData | null
  psychologyDoctorId: number | null
  isCustomizePackageDrawerOpen: boolean
}

const initialState: PackagesState = {
  isLoading: false,
  packages: [],
  activePackage: null,
  activePsychologyVariant: null,
  psychologyDoctorId: null,
  isCustomizePackageDrawerOpen: false
}

export const packagesSlice = createSlice({
  name: 'packages',
  initialState,
  reducers: {
    resetPackagesState: (state) => {
      state = initialState
      return state
    },
    onCustomizePackageDrawerOpen: (state) => {
      state.isCustomizePackageDrawerOpen = true
    },
    onCustomizePackageDrawerClose: (state) => {
      state.isCustomizePackageDrawerOpen = false
    },
    setActivePackage: (state, action: PayloadAction<PackageData | null>) => {
      state.activePackage = action.payload
    },
    updateActivePackage: (state, action: PayloadAction<Partial<PackageData>>) => {
      if (state.activePackage) {
        state.activePackage = { ...state.activePackage, ...action.payload }
      }
    },
    setActivePsychologyVariant: (state, action: PayloadAction<PsychologyVariantData>) => {
      state.activePsychologyVariant = action.payload
    },
    setPsychologyDoctorId: (state, action: PayloadAction<number | null>) => {
      state.psychologyDoctorId = action.payload
    }
  },
  extraReducers: (builder) =>
    builder
      .addCase(getPackages.pending, (state) => {
        state.isLoading = true
      })
      .addCase(getPackages.fulfilled, (state, action) => {
        state.isLoading = false
        state.packages = action.payload
      })
      .addCase(getPackages.rejected, (state) => {
        state.isLoading = false
      })
})

export const {
  resetPackagesState,
  onCustomizePackageDrawerOpen,
  onCustomizePackageDrawerClose,
  setActivePackage,
  updateActivePackage,
  setActivePsychologyVariant,
  setPsychologyDoctorId
} = packagesSlice.actions

export default packagesSlice.reducer

interface GetPackagesParams {
  patientId: number
}

export const getPackages = createAsyncThunk(
  'get-packages',
  async ({ patientId }: GetPackagesParams, { rejectWithValue }) => {
    try {
      const { data } = await nestApiService.apiInstance.get<PackageData[]>(`/packages/${patientId}`)

      const groupedPackagesByProductId = groupBy(data, ({ name, drug }) => `${drug[0].productId}:${name}`)

      return (
        Object.values(groupedPackagesByProductId).map((packages) => {
          const currentPackage = packages[0]
          const remainingPackages = [packages.slice(1)]

          return mergeWith(currentPackage, ...remainingPackages, (objValue: any, srcValue: any) => {
            if (isArray(objValue)) {
              if (srcValue[0]?.id) return uniqBy(objValue.concat(srcValue), 'id')
              return uniq(objValue.concat(srcValue))
            }
          })
        }) as PackageData[]
      ).map((p) => {
        const allDrugs = p.drug
        const drugsGroupedByProductId = groupBy(allDrugs, 'productId')

        const mergedDrugs = flatten(
          Object.values(drugsGroupedByProductId).map((drugs) => {
            const drugWithPriceMonthly = drugs.find((d) => !!d.priceMonthly)
            const drugWithPriceQuarterly = drugs.find((d) => !!d.priceQuarterly)

            return drugs.map((d) => {
              const isMonthlyOnly = !!d.priceMonthly && !d.priceQuarterly
              const isQuarterlyOnly = !!d.priceQuarterly && !d.priceMonthly
              return {
                ...d,
                isMonthlyOnly,
                isQuarterlyOnly,
                monthlyVariantId: drugWithPriceMonthly?.variantId || null,
                quarterlyVariantId: drugWithPriceQuarterly?.variantId || null,
                priceMonthly: drugWithPriceMonthly?.priceMonthly || null,
                priceQuarterly: drugWithPriceQuarterly?.priceQuarterly || null,
                presentationMonthly: drugWithPriceMonthly?.presentation || null,
                presentationQuarterly: drugWithPriceQuarterly?.presentation || null
              }
            })
          })
        )

        return {
          ...p,
          drug: mergedDrugs
        }
      }) as PackageData[]
    } catch (error) {
      return rejectWithValue(error)
    }
  }
)

export interface CustomCepData extends CEP {
  addressNumber?: string
  complement?: string
  region?: string
}

export interface PackageOrderResponse {
  xis?: boolean
  recovered?: boolean
  spreeOrderId?: number
  offerId?: string
  offerName?: string
  status?: string
  totalCents?: number
  packageOrderId: number
  patientId: number
  pixQrCode?: string
  paymentId?: number
  spreeAddressId?: number
}

export interface VariantData {
  price: number
  variantId: string | number
}

export interface CourseVariantData extends VariantData {
  slug: CourseSlug
  description: string
  trailerSrc: string
  thumbSrc: string
}

export interface PhytoVariantData extends VariantData {
  label: string
  description: string
  imgSrc: string
  descriptionSrc: string
  amount: number
}

export interface PsychologyVariantData extends VariantData {
  amount: number
  medium?: PsychologyMedium
}

export interface ShipmentData {
  xisDeliveryQuote?: string
  // xFarm returns its selected SLA ID rather than a Correios carrier name.
  deliveryType: string
  deliveryDays: number
  deliveryCost: number
  region?: string
}
